import React, { useState } from 'react';
import axios from 'axios';
import { Download, Printer, Mail, ShieldCheck } from 'lucide-react';
import { fmtEuro, fmtDateFr, LITERIE } from './devisCalc';
import { genererPdf } from './devisPdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Un seul récapitulatif structuré, partagé par l'écran, le PDF, l'impression et l'email
export const buildRecap = (devis, weeks, { pickupLabel, withInsurance, regles }) => {
  const dates = `${fmtDateFr(weeks[0].start)} au ${fmtDateFr(weeks[weeks.length - 1].end)}`;
  const chambres = devis.chambres.map((c) => {
    const lignes = c.lines.map((l) => ({ label: `CHAMBRE ${c.num} — voyageur ${l.pos} (${l.statut}, ${l.age} ans)`, montant: l.total }));
    if (c.isSingle) lignes.push({ label: `Supplément chambre individuelle — Chambre ${c.num}`, montant: c.supplement });
    if (c.piscine) lignes.push({ label: `Vue piscine garantie — Chambre ${c.num}`, montant: c.piscine });
    const literie = c.isSingle && c.travs.length === 1 ? 'Literie : chambre individuelle — un lit simple (pas de grand lit).' : (LITERIE[c.literie] || LITERIE['']);
    return { num: c.num, lignes, literie, soustotal: c.hotel, optimized: !!c.optimized };
  });
  const transport = [
    { label: `Transport aller${devis.aller ? ` (${devis.chambres.reduce((s, c) => s + c.transport.nAller, 0)} pers. × ${weeks[0].prixTrajet} €)` : ''}`, montant: devis.aller },
    { label: `Transport retour${devis.retour ? ` (${devis.chambres.reduce((s, c) => s + c.transport.nRetour, 0)} pers. × ${weeks[weeks.length - 1].prixTrajet} €)` : ''}`, montant: devis.retour },
  ];
  const extras = [{ label: 'Adhésion annuelle (par famille)', montant: devis.adhesion }];
  if (withInsurance) extras.push({ label: `Assurance multirisque (${devis.nb} pers. × ${devis.prixAssurance} €)`, montant: devis.assurance });
  return { sejour: { titre: weeks.map((w) => w.label).join(' + '), dates, nuits: devis.nights }, depart: devis.hasCar ? pickupLabel : '', chambres, transport, extras, total: devis.total, nb: devis.nb, prixAssurance: devis.prixAssurance };
};

const Row = ({ label, montant, bold, testid, muted }) => (
  <tr className={`border-t border-gray-200 ${bold ? 'bg-[#EEF3F8] font-bold' : ''}`} data-testid={testid}>
    <td className={`px-3 py-2.5 ${muted ? 'text-gray-500 italic text-xs' : 'text-[#1E3A5F]'}`}>{label}</td>
    <td className="px-3 py-2.5 text-right whitespace-nowrap text-[#1E3A5F]">{montant === null ? '' : fmtEuro(montant)}</td>
  </tr>
);

export const RecapTable = ({ recap, print }) => (
  <table className="w-full text-sm" data-testid={print ? 'print-table' : 'recap-table'}>
    <thead className="bg-[#EEF3F8] text-[#1E3A5F]"><tr><th className="px-3 py-3 text-left text-xs font-bold">Désignation</th><th className="px-3 py-3 text-right text-xs font-bold">Montant</th></tr></thead>
    <tbody>
      {recap.chambres.map((c) => (
        <React.Fragment key={c.num}>
          {c.lignes.map((l, i) => <Row key={i} label={l.label} montant={l.montant} />)}
          <Row label={c.literie} montant={null} muted />
          <Row label={`Sous-total chambre ${c.num}`} montant={c.soustotal} bold testid={print ? undefined : `soustotal-${c.num}`} />
        </React.Fragment>
      ))}
      {recap.transport.map((l, i) => <Row key={`t${i}`} label={l.label} montant={l.montant} testid={print ? undefined : `transport-${i ? 'retour' : 'aller'}`} />)}
      {recap.extras.map((l, i) => <Row key={`e${i}`} label={l.label} montant={l.montant} />)}
    </tbody>
  </table>
);

const Total = ({ total, nb, print }) => (
  <div className="flex items-center justify-between bg-[#CDEFD2] text-[#0B5D1E] rounded-b-xl px-4 py-4" data-testid={print ? undefined : 'total-general'}>
    <span className="font-black text-sm sm:text-base tracking-wide">TOTAL GÉNÉRAL À PAYER <span className="font-semibold text-xs">({nb} voyageur{nb > 1 ? 's' : ''})</span></span>
    <span className="font-black text-2xl sm:text-3xl">{fmtEuro(total)}</span>
  </div>
);

export const DevisPrint = ({ recap, annee }) => (
  <div id="devis-print" className="hidden bg-white text-black p-6">
    <h1 className="text-2xl font-black text-[#1E3A5F]">HÔTEL OASIS PARK</h1>
    <h2 className="text-lg font-bold text-[#1E3A5F] mb-1">VOTRE DEVIS — LLORET DEL MAR {annee}</h2>
    <p className="text-sm mb-1"><strong>{recap.sejour.titre}</strong> — du {recap.sejour.dates} ({recap.sejour.nuits} nuits)</p>
    {recap.depart && <p className="text-sm mb-1">Point de départ du car : <strong>{recap.depart}</strong></p>}
    <div className="border border-gray-300 rounded-xl overflow-hidden mt-3">
      <RecapTable recap={recap} print />
      <Total total={recap.total} nb={recap.nb} print />
    </div>
    <p className="text-xs text-gray-600 mt-3">Le grand lit est une préférence, non une garantie : pour les couples, la chambre peut être aménagée avec deux lits simples rapprochés, selon les disponibilités de l'hôtel. La vue piscine est une option garantie avec supplément.</p>
    <p className="text-xs text-gray-600 mt-1">Devis indicatif établi par Rhône Alpes Amitié Voyages — le montant définitif est confirmé lors de l'inscription. rhoneamitie@gmail.com</p>
  </div>
);

export const DevisRecap = ({ recap, annee, incomplet, bloque, withInsurance, setWithInsurance, regles, prefill, inscriptionsOuvertes }) => {
  const inscrire = () => {
    try { sessionStorage.setItem('raav_devis_prefill', JSON.stringify(prefill)); } catch { /* stockage indisponible */ }
    window.location.href = '/inscription?devis=1';
  };
  const [email, setEmail] = useState('');
  const [etat, setEtat] = useState(null);
  const envoyer = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setEtat({ err: 'Adresse email invalide' }); return; }
    setEtat({ busy: true });
    try {
      const r = await axios.post(`${API}/devis-espagne/email`, { email, recap, annee });
      setEtat(r.data.envoye ? { ok: `Devis envoyé à ${email}` } : { err: "L'envoi n'a pas pu être effectué, réessayez dans un instant." });
    } catch (e) { setEtat({ err: e.response?.data?.detail || "L'envoi a échoué." }); }
  };
  return (
    <div data-testid="devis-recap">
      <label className="flex items-center gap-3 rounded-xl border-2 border-gray-200 px-4 py-3 mb-4 cursor-pointer hover:border-[#1E3A5F]/40">
        <input type="checkbox" checked={withInsurance} onChange={(e) => setWithInsurance(e.target.checked)} className="w-5 h-5 accent-[#1E3A5F]" data-testid="assurance-checkbox" />
        <span className="flex-1"><span className="font-bold text-[#1E3A5F] flex items-center gap-2"><ShieldCheck size={16} /> Assurance multirisque voyage</span><span className="text-xs text-gray-500">{regles.assurance} € par personne — annulation, assistance, bagages (optionnelle)</span></span>
      </label>
      {(incomplet || bloque) ? (
        <div className="rounded-xl border-2 border-dashed border-amber-400 bg-amber-50 px-4 py-6 text-center text-amber-900 font-semibold" data-testid="devis-incomplet">
          {bloque ? 'Maximum 3 adultes par chambre : répartissez les voyageurs sur 2 chambres.' : 'Devis incomplet — renseignez une date de naissance valide pour chaque voyageur.'}
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <RecapTable recap={recap} />
            <Total total={recap.total} nb={recap.nb} />
          </div>
          <div className="mt-5 grid grid-cols-1 gap-3">
            <button onClick={() => genererPdf(recap, annee)} className="w-full flex items-center justify-center gap-3 bg-[#0B63CE] hover:bg-[#0952AB] text-white font-black py-4 rounded-xl text-sm sm:text-base transition-colors" data-testid="btn-pdf"><Download size={20} /> TÉLÉCHARGER MON DEVIS EN PDF</button>
            <button onClick={() => window.print()} className="w-full flex items-center justify-center gap-3 bg-[#0B63CE] hover:bg-[#0952AB] text-white font-black py-4 rounded-xl text-sm sm:text-base transition-colors" data-testid="btn-print"><Printer size={20} /> IMPRIMER EN PDF</button>
            <div className="rounded-xl border-2 border-[#0B7A3B]/30 p-3 bg-emerald-50/40" data-testid="email-bloc">
              <div className="flex flex-col sm:flex-row gap-2">
                <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setEtat(null); }} placeholder="votre@email.fr" className="flex-1 bg-[#FFE066] border-2 border-[#F5C400] rounded-lg px-3 py-3 text-sm font-semibold text-[#1E3A5F] placeholder:text-[#1E3A5F]/40" data-testid="email-input" aria-label="Votre adresse email" />
                <button onClick={envoyer} disabled={etat?.busy} className="flex items-center justify-center gap-3 bg-[#0B7A3B] hover:bg-[#09662F] disabled:opacity-60 text-white font-black py-3 px-5 rounded-xl text-sm sm:text-base transition-colors" data-testid="btn-email"><Mail size={20} /> {etat?.busy ? 'ENVOI…' : 'ENVOYER LE DEVIS PAR EMAIL'}</button>
              </div>
              <p className="text-[11px] text-gray-600 mt-2">Le devis complet vous est envoyé par l'association à cette adresse (copie à l'association).</p>
              {etat?.ok && <p className="text-sm font-bold text-emerald-700 mt-1" data-testid="email-ok">✅ {etat.ok}</p>}
              {etat?.err && <p className="text-sm font-bold text-red-700 mt-1" data-testid="email-err">{etat.err}</p>}
            </div>
            {inscriptionsOuvertes ? (
              <>
                <button onClick={inscrire} className="w-full flex items-center justify-center gap-2 border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white font-bold py-3 rounded-xl text-sm transition-colors" data-testid="btn-inscription">Je m'inscris en ligne avec ce devis →</button>
                <p className="text-[11px] text-gray-500 -mt-1">Le formulaire d'inscription reprend votre séjour, les dates de naissance, les chambres, les trajets et le point de départ.</p>
              </>
            ) : (
              <p className="text-sm text-[#1E3A5F] bg-[#EEF3F8] rounded-xl px-4 py-3 text-center font-semibold" data-testid="inscriptions-bientot">Les inscriptions pour ces dates ouvrent bientôt : gardez ce devis (PDF ou email), vous pourrez vous inscrire en ligne dès l'ouverture.</p>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-4">Devis indicatif : le montant définitif est confirmé lors de l'inscription. Le grand lit est une préférence non garantie ; la vue piscine est une option garantie avec supplément.</p>
        </>
      )}
    </div>
  );
};
